/**
 * Système de logging conditionnel pour le frontend
 *
 * Usage:
 *   import logger from './utils/logger';
 *   logger.info('Message informatif');
 *   logger.debug('Message de debug');
 *   logger.warn('Avertissement');
 *   logger.error('Erreur', error);
 *
 * En production (NODE_ENV=production ou REACT_APP_ENV=production),
 * seuls error et warn sont affichés
 * En développement, tous les logs sont affichés
 *
 * Filtrage des logs bruyants (token/WebSocket) :
 *   - Par défaut, les erreurs token/WebSocket sont masquées
 *   - Pour les afficher : localStorage.setItem('debug_auth', 'true')
 *   - Pour les masquer : localStorage.removeItem('debug_auth')
 */

const isDevelopment =
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_ENV === 'development' ||
  !process.env.NODE_ENV;

/**
 * Patterns de logs à filtrer (token, JWT, WebSocket, session)
 * Ces logs sont nombreux et polluent la console - masqués par défaut
 */
const NOISY_PATTERNS = [
  /token/i,
  /jwt/i,
  /websocket/i,
  /socket\.io/i,
  /authentification/i,
  /auth.*failed/i,
  /expired/i,
  /timeout.*auth/i,
  /reconnect/i,
  /déconnexion/i,
  /connexion.*error/i
];

/**
 * Vérifie si le mode debug auth est activé
 */
const isAuthDebugEnabled = () => {
  try {
    return localStorage.getItem('debug_auth') === 'true';
  } catch {
    return false;
  }
};

/**
 * Vérifie si un message correspond aux patterns bruyants
 */
const isNoisyLog = (args) => {
  const message = args.map(arg =>
    typeof arg === 'string' ? arg :
    typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
  ).join(' ');

  return NOISY_PATTERNS.some(pattern => pattern.test(message));
};

/**
 * Filtre les logs bruyants sauf si debug_auth est activé
 */
const shouldLog = (args, forceShow = false) => {
  if (forceShow) return true;
  if (isAuthDebugEnabled()) return true;
  return !isNoisyLog(args);
};

const logger = {
  /**
   * Logs d'information (uniquement en développement)
   */
  info: (...args) => {
    if (isDevelopment && shouldLog(args)) {
      console.log('%c[INFO]', 'color: #2196F3', ...args);
    }
  },

  /**
   * Logs de debug (uniquement en développement)
   */
  debug: (...args) => {
    if (isDevelopment && shouldLog(args)) {
      console.log('%c[DEBUG]', 'color: #9E9E9E', ...args);
    }
  },

  /**
   * Avertissements (filtrés si bruyants)
   */
  warn: (...args) => {
    if (shouldLog(args)) {
      console.warn('%c[WARN]', 'color: #FF9800', ...args);
    }
  },

  /**
   * Erreurs (filtrées si bruyantes, sinon toujours affichées)
   */
  error: (...args) => {
    if (shouldLog(args)) {
      console.error('%c[ERROR]', 'color: #F44336', ...args);
    }
  },

  /**
   * Succès (uniquement en développement)
   */
  success: (...args) => {
    if (isDevelopment && shouldLog(args)) {
      console.log('%c[SUCCESS] ✅', 'color: #4CAF50', ...args);
    }
  },

  /**
   * Force l'affichage d'un log même s'il correspond aux patterns bruyants
   */
  forceError: (...args) => {
    console.error('%c[ERROR]', 'color: #F44336', ...args);
  },

  /**
   * Active/désactive le debug des logs auth/token/WebSocket
   */
  setAuthDebug: (enabled) => {
    try {
      if (enabled) {
        localStorage.setItem('debug_auth', 'true');
        console.log('%c[LOGGER] Debug auth/token/WebSocket ACTIVÉ', 'color: #4CAF50; font-weight: bold');
      } else {
        localStorage.removeItem('debug_auth');
        console.log('%c[LOGGER] Debug auth/token/WebSocket DÉSACTIVÉ', 'color: #FF9800; font-weight: bold');
      }
    } catch (e) {
      console.warn('Impossible de modifier localStorage:', e);
    }
  },

  /**
   * Affiche l'état actuel du filtrage
   */
  status: () => {
    const enabled = isAuthDebugEnabled();
    console.log(
      `%c[LOGGER] Logs auth/token/WebSocket : ${enabled ? 'VISIBLES' : 'MASQUÉS'}`,
      `color: ${enabled ? '#4CAF50' : '#FF9800'}; font-weight: bold`
    );
    console.log('Pour activer: logger.setAuthDebug(true)');
    console.log('Pour désactiver: logger.setAuthDebug(false)');
  }
};

// Exposer logger sur window pour debug depuis la console
if (typeof window !== 'undefined') {
  window.logger = logger;
}

export default logger;
